/**
 * Vehicle Selector Component
 * Switches the chat between knowledge-graph systems (see config/assets.js)
 */

import React from 'react';
import { CHAT_ASSETS } from '../config/assets';
import './VehicleSelector.css';

const VEHICLES = Object.entries(CHAT_ASSETS).map(([id, asset]) => ({ id, ...asset }));

const VehicleSelector = ({ selectedVehicle = 'nexon', onVehicleChange }) => {
  // State for showing/hiding compressor option
  const [showCompressor, setShowCompressor] = React.useState(() => {
    try {
      const saved = localStorage.getItem('show_compressor');
      return saved !== 'false'; // Default to true, hide only if explicitly set to false
    } catch (e) {
      return true;
    }
  });

  const currentVehicle = VEHICLES.find(v => v.id === selectedVehicle) || VEHICLES[0];

  const handleVehicleChange = (vehicleId) => {
    if (vehicleId !== selectedVehicle && onVehicleChange) {
      onVehicleChange(vehicleId);

      // Save to localStorage for persistence
      try {
        localStorage.setItem('selected_vehicle', vehicleId);
      } catch (e) {
        console.warn('Failed to save vehicle selection to localStorage:', e);
      }
    }
  };

  const toggleCompressor = () => {
    const newValue = !showCompressor;
    setShowCompressor(newValue);
    try {
      localStorage.setItem('show_compressor', String(newValue));
    } catch (e) {
      console.warn('Failed to save compressor visibility to localStorage:', e);
    }
  };

  // Filter vehicles based on showCompressor state
  const visibleVehicles = showCompressor
    ? VEHICLES
    : VEHICLES.filter(v => v.id !== 'compressor');

  return (
    <div className="vehicle-selector">
      <div className="vehicle-selector-label">
        Vehicle:
        <button
          className="compressor-toggle-btn"
          onClick={toggleCompressor}
          title={showCompressor ? 'Hide compressor' : 'Show compressor'}
        >
          {showCompressor ? '👁️' : '👁️‍🗨️'}
        </button>
      </div>
      <div className="vehicle-selector-options">
        {visibleVehicles.map((vehicle) => (
          <button
            key={vehicle.id}
            className={`vehicle-option ${selectedVehicle === vehicle.id ? 'active' : ''}`}
            onClick={() => handleVehicleChange(vehicle.id)}
            style={{
              '--vehicle-color': vehicle.color,
            }}
            title={`${vehicle.name} - ${vehicle.description}`}
          >
            <span className="vehicle-icon">{vehicle.icon}</span>
            <span className="vehicle-name">{vehicle.name}</span>
          </button>
        ))}
      </div>

      {/* Vehicle Info Badge */}
      <div
        className="vehicle-info-badge"
        style={{
          '--vehicle-color': currentVehicle.color,
        }}
      >
        <div className="vehicle-info-content">
          <span className="vehicle-info-icon">{currentVehicle.icon}</span>
          <div className="vehicle-info-text">
            <div className="vehicle-info-name">{currentVehicle.name}</div>
            <div className="vehicle-info-description">{currentVehicle.description}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to get initial vehicle from localStorage
export const usePersistedVehicle = () => {
  const [vehicle, setVehicle] = React.useState(() => {
    try {
      const saved = localStorage.getItem('selected_vehicle');
      return saved || 'nexon';
    } catch (e) {
      return 'nexon';
    }
  });

  return [vehicle, setVehicle];
};

export default VehicleSelector;
